import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import {
  useCreateVeilleMutation,
  useGetCurrentUserVeilleQuotasQuery,
  useGetUserVeillesQuery,
  useGetVeilleByIdQuery,
  useGetVeilleFeedQuery,
  useGetVeilleLiveCurveQuery,
  usePauseVeilleMutation,
  useRenameVeilleMutation,
  useResumeVeilleMutation,
  useStopVeilleMutation,
} from '@/shared/hooks/queries/generated';
import i18n from '@/shared/i18n';

import { mapVeille, mapVeilleFeedItem, type VeilleView } from './veilleTypes';

// ============================================================================
// Hooks de consommation de l'API veille RÉELLE.
// Cadences du contrat : direct sondé toutes les 30 s (le cache serveur de la
// courbe ne rend rien de plus frais), feed paginé keyset avec arrêt STRICT
// sur nextCursor === null, liste rafraîchie régulièrement.
// ============================================================================

const VEILLES_KEY = ['GetUserVeilles'];
const QUOTAS_KEY = ['GetCurrentUserVeilleQuotas'];
const LIVE_POLL_MS = 30_000;
// Courbe à la minute (2026-08-14) : cache serveur 5 s sur la queue — sonder
// plus vite ne rend rien de plus frais, plus lentement gâche la résolution
const CURVE_POLL_MS = 5_000;

/** Liste des veilles (stopped exclues : elles ont libéré leur slot). */
export const useUserVeilles = () => {
  const { data, isLoading, error } = useGetUserVeillesQuery(undefined, {
    refetchInterval: 60_000,
  });
  const veilles = useMemo(
    (): VeilleView[] => (data?.getUserVeilles ?? []).map(mapVeille).filter((veille) => veille.state !== 'stopped'),
    [data],
  );
  return { veilles, isLoading, error };
};

/** Quotas de veilles simultanées, servis par le back (source de vérité). */
export const useVeilleQuotas = () => {
  const { data } = useGetCurrentUserVeilleQuotasQuery(undefined, { staleTime: 30_000 });
  return data?.getCurrentUserVeilleQuotas ?? null;
};

/**
 * Détail d'une veille — sondée toutes les 5 s tant que le backfill des 7
 * jours d'historique tourne (queued/running), pour basculer les statistiques
 * dès qu'elles existent.
 */
export const useVeilleDetail = (id: string | undefined) => {
  const { data, isLoading } = useGetVeilleByIdQuery(
    { id: id ?? '' },
    {
      enabled: !!id,
      refetchInterval: (query: { state: { data?: { getVeilleById?: { historyState?: string } | null } } }) => {
        const historyState = query.state.data?.getVeilleById?.historyState;
        return historyState === 'queued' || historyState === 'running' ? 5_000 : false;
      },
    },
  );
  const veille = useMemo(() => (data?.getVeilleById ? mapVeille(data.getVeilleById) : undefined), [data]);
  return { veille, isLoading, notFound: !isLoading && data != null && data.getVeilleById == null };
};

/**
 * Feed des ventes détectées : pagination keyset (mêmes windowMinutes/limit à
 * chaque page, arrêt sur nextCursor === null — une page vide avec curseur
 * n'est PAS la fin), resondé toutes les 30 s.
 *
 * ⚠️⚠️ `windowMinutes` est envoyé à `null` DÉLIBÉRÉMENT : le serveur applique
 * alors son défaut, qui vaut toute la rétention de `sale_event` (14 jours).
 * Il a été figé à `360` ici, par alignement sur la courbe, et c'est ce qui a
 * rendu INVISIBLE le passage du serveur à 14 jours (2026-09-09) : l'API servait
 * bien la fenêtre large, le front continuait de lui demander six heures. Ne pas
 * y remettre une constante « pour être explicite » — ce serait replafonner le
 * feed sans qu'aucune erreur ne le signale. Cet argument ne sert qu'à
 * RESTREINDRE volontairement (un futur sélecteur « dernières 24 h »).
 */
export const useVeilleFeed = (veilleId: string | undefined, enabled: boolean) => {
  const query = useInfiniteQuery({
    queryKey: ['GetVeilleFeed', veilleId],
    queryFn: ({ pageParam }) =>
      // Curseur lié à la fenêtre : mêmes windowMinutes/limit à CHAQUE page
      useGetVeilleFeedQuery.fetcher({ veilleId: veilleId ?? '', windowMinutes: null, limit: 50, after: pageParam ?? null })(),
    initialPageParam: null as string | null,
    // Arrêt STRICT : nextCursor null => plus rien (jamais « page vide »)
    getNextPageParam: (lastPage) => lastPage.getVeilleFeed.nextCursor ?? undefined,
    enabled: enabled && !!veilleId,
    refetchInterval: LIVE_POLL_MS,
  });

  const products = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.getVeilleFeed.items).map(mapVeilleFeedItem),
    [query.data],
  );

  // Budget de lecture serveur : une page peut être VIDE et porter un curseur
  // (veille à similarité, lecture par tranches coupée à 12 s). On enchaîne
  // automatiquement — borné pour ne pas marteler le serveur — et on l'expose
  // comme un état « balayage en cours », pas comme « aucune vente ».
  const AUTO_SCAN_MAX_PAGES = 8;
  const pages = query.data?.pages ?? [];
  const isScanning =
    products.length === 0 && query.hasNextPage === true && pages.length > 0 && pages.length < AUTO_SCAN_MAX_PAGES;
  useEffect(() => {
    if (isScanning && !query.isFetchingNextPage) {
      void query.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScanning, query.isFetchingNextPage, pages.length]);

  return {
    products,
    isLoading: query.isLoading,
    /** Fenêtre en cours de balayage par tranches : rien à montrer ENCORE. */
    isScanning,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
};

/**
 * Courbe des 6 dernières heures — sondage 30 s (cache serveur : inutile plus
 * vite). Une erreur est TRANSITOIRE : on garde l'affichage précédent
 * (placeholderData) et on retente au tour suivant.
 */
export const useVeilleLiveCurve = (veilleId: string | undefined, enabled: boolean) => {
  const { data, isLoading } = useGetVeilleLiveCurveQuery(
    { veilleId: veilleId ?? '' },
    {
      enabled: enabled && !!veilleId,
      refetchInterval: CURVE_POLL_MS,
      placeholderData: (previous?: import('@/shared/hooks/queries/generated').GetVeilleLiveCurveQuery) => previous,
      retry: 1,
    },
  );
  return { points: data?.getVeilleLiveCurve ?? [], isLoading };
};

/** Cycle de vie : pause / reprise / arrêt définitif / renommage. */
export const useVeilleLifecycle = () => {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: VEILLES_KEY });
    void queryClient.invalidateQueries({ queryKey: QUOTAS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['GetVeilleById'] });
  };

  const pauseMutation = usePauseVeilleMutation({ onSuccess: invalidate });
  const resumeMutation = useResumeVeilleMutation({ onSuccess: invalidate });
  const stopMutation = useStopVeilleMutation({ onSuccess: invalidate });
  const renameMutation = useRenameVeilleMutation({ onSuccess: invalidate });

  return {
    pause: (id: string) => pauseMutation.mutateAsync({ id }),
    resume: (id: string) => resumeMutation.mutateAsync({ id }),
    stop: (id: string) => stopMutation.mutateAsync({ id }),
    rename: (id: string, name: string) => renameMutation.mutateAsync({ id, name }),
  };
};

export interface CreateVeilleResult {
  success: boolean;
  veilleId?: string;
  error?: string;
}

/** Création — exactement UN de criteria / sellerVintedId (règle serveur). */
export const useCreateVeille = () => {
  const queryClient = useQueryClient();
  const createMutation = useCreateVeilleMutation();

  const createVeille = async (input: {
    name: string;
    criteria?: Record<string, unknown>;
    sellerVintedId?: string;
    sourceAnalysisId?: string;
  }): Promise<CreateVeilleResult> => {
    try {
      const result = await createMutation.mutateAsync({ input });
      void queryClient.invalidateQueries({ queryKey: VEILLES_KEY });
      void queryClient.invalidateQueries({ queryKey: QUOTAS_KEY });
      return { success: true, veilleId: result.createVeille.id };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : i18n.t('veille.createError') };
    }
  };

  return { createVeille, isCreating: createMutation.isPending };
};
